import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import Button from '../../../components/Button';
import type { FeedbackQuestion, QuestionType, MultiSelectConfig, RatingConfig } from '../../../types/dynamic-feedback';

interface QuestionBuilderProps {
  questions: FeedbackQuestion[];
  onChange: (questions: FeedbackQuestion[]) => void;
}

export const QuestionBuilder = ({ questions, onChange }: QuestionBuilderProps) => {
  const addRatingQuestion = () => {
    const newQuestion: FeedbackQuestion = {
      order: questions.length,
      type: 'rating',
      question: '',
      required: false,
      config: {
        min: 1,
        max: 10,
        labels: { min: 'Not at all', max: 'Extremely' },
      },
    };
    onChange([...questions, newQuestion]);
  };

  const addMultiSelectQuestion = () => {
    const newQuestion: FeedbackQuestion = {
      order: questions.length,
      type: 'multi_select',
      question: '',
      required: false,
      config: {
        options: [],
        allowMultiple: false,
      },
    };
    onChange([...questions, newQuestion]);
  };

  const addTextQuestion = () => {
    const newQuestion: FeedbackQuestion = {
      order: questions.length,
      type: 'text',
      question: '',
      required: false,
    };
    onChange([...questions, newQuestion]);
  };

  const updateQuestion = (index: number, updates: Partial<FeedbackQuestion>) => {
    onChange(
      questions.map((q, i) =>
        i === index ? { ...q, ...updates } : q
      )
    );
  };

  const deleteQuestion = (index: number) => {
    const filtered = questions.filter((_, i) => i !== index);
    // Reorder
    onChange(filtered.map((q, i) => ({ ...q, order: i })));
  };

  const moveQuestion = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= questions.length) return;

    const reordered = [...questions];
    [reordered[index], reordered[newIndex]] = [reordered[newIndex], reordered[index]];
    onChange(reordered.map((q, i) => ({ ...q, order: i })));
  };

  const getQuestionTypeLabel = (type: QuestionType): string => {
    switch (type) {
      case 'rating':
        return 'Rating';
      case 'multi_select':
        return 'Multi-Select';
      case 'text':
        return 'Text';
      default:
        return type;
    }
  };

  const addMultiSelectOption = (questionIndex: number) => {
    const question = questions[questionIndex];
    if (!question || question.type !== 'multi_select') return;

    const newOption = {
      id: crypto.randomUUID(),
      label: '',
      emoji: '',
    };

    const currentConfig = question.config as MultiSelectConfig | undefined;
    updateQuestion(questionIndex, {
      config: {
        ...currentConfig,
        options: [...(currentConfig?.options || []), newOption],
      } as MultiSelectConfig,
    });
  };

  const updateMultiSelectOption = (
    questionIndex: number,
    optionId: string,
    field: 'label' | 'emoji',
    value: string
  ) => {
    const question = questions[questionIndex];
    if (!question || question.type !== 'multi_select' || !question.config) return;

    const currentConfig = question.config as MultiSelectConfig;
    updateQuestion(questionIndex, {
      config: {
        ...currentConfig,
        options: currentConfig.options.map((opt: { id: string; label: string; emoji?: string }) =>
          opt.id === optionId ? { ...opt, [field]: value } : opt
        ),
      },
    });
  };

  const deleteMultiSelectOption = (questionIndex: number, optionId: string) => {
    const question = questions[questionIndex];
    if (!question || question.type !== 'multi_select' || !question.config) return;

    const currentConfig = question.config as MultiSelectConfig;
    updateQuestion(questionIndex, {
      config: {
        ...currentConfig,
        options: currentConfig.options.filter((opt: { id: string }) => opt.id !== optionId),
      },
    });
  };

  return (
    <div className="space-y-4">
      {questions.map((question, index) => (
        <div key={index} className="border rounded-lg p-4 space-y-3 bg-white">
          {/* Question Header */}
          <div className="flex items-start gap-2">
            <div className="flex flex-col gap-1 mt-2">
              <button
                onClick={() => moveQuestion(index, 'up')}
                disabled={index === 0}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                title="Move up"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
              <button
                onClick={() => moveQuestion(index, 'down')}
                disabled={index === questions.length - 1}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                title="Move down"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>

            <div className="flex-1 min-w-0 space-y-3">
              {/* Type and Required */}
              <div className="flex flex-wrap gap-2 items-center">
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                  {getQuestionTypeLabel(question.type)}
                </span>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={question.required}
                    onChange={(e) => updateQuestion(index, { required: e.target.checked })}
                  />
                  <span className="text-sm">Required</span>
                </label>
              </div>

              {/* Question Text */}
              <input
                type="text"
                value={question.question}
                onChange={(e) => updateQuestion(index, { question: e.target.value })}
                placeholder="Enter your question..."
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              {/* Multi-Select Config */}
              {question.type === 'multi_select' && (() => {
                const multiConfig = question.config as MultiSelectConfig | undefined;
                return (
                  <div className="space-y-2 pl-4 border-l-2 border-gray-200">
                    <div className="flex gap-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={multiConfig?.allowMultiple || false}
                          onChange={(e) =>
                            updateQuestion(index, {
                              config: { ...multiConfig, allowMultiple: e.target.checked } as MultiSelectConfig,
                            })
                          }
                        />
                        <span className="text-sm">Allow Multiple</span>
                      </label>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">Options</span>
                        <button
                          onClick={() => addMultiSelectOption(index)}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          + Add Option
                        </button>
                      </div>
                      {multiConfig?.options?.map((option: { id: string; label: string; emoji?: string }) => (
                        <div key={option.id} className="flex gap-2">
                          <input
                            type="text"
                            value={option.emoji}
                            onChange={(e) =>
                              updateMultiSelectOption(index, option.id, 'emoji', e.target.value)
                            }
                            placeholder="🎯"
                            className="w-12 px-2 py-1 border rounded text-center"
                          />
                          <input
                            type="text"
                            value={option.label}
                            onChange={(e) =>
                              updateMultiSelectOption(index, option.id, 'label', e.target.value)
                            }
                            placeholder="Option label"
                            className="flex-1 min-w-0 px-2 py-1 border rounded"
                          />
                          <button
                            onClick={() => deleteMultiSelectOption(index, option.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Rating Config */}
              {question.type === 'rating' && (() => {
                const ratingConfig = question.config as RatingConfig | undefined;
                return (
                  <div className="space-y-2 pl-4 border-l-2 border-gray-200">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Min</label>
                        <input
                          type="number"
                          value={ratingConfig?.min || 1}
                          onChange={(e) =>
                            updateQuestion(index, {
                              config: {
                                ...ratingConfig,
                                min: parseInt(e.target.value) || 1,
                                max: ratingConfig?.max || 10,
                                labels: ratingConfig?.labels || { min: 'Not at all', max: 'Extremely' },
                              } as RatingConfig,
                            })
                          }
                          className="w-full px-2 py-1 border rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Max</label>
                        <input
                          type="number"
                          value={ratingConfig?.max || 10}
                          onChange={(e) =>
                            updateQuestion(index, {
                              config: {
                                ...ratingConfig,
                                min: ratingConfig?.min || 1,
                                max: parseInt(e.target.value) || 10,
                                labels: ratingConfig?.labels || { min: 'Not at all', max: 'Extremely' },
                              } as RatingConfig,
                            })
                          }
                          className="w-full px-2 py-1 border rounded"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Min Label</label>
                        <input
                          type="text"
                          value={ratingConfig?.labels?.min || 'Not at all'}
                          onChange={(e) =>
                            updateQuestion(index, {
                              config: {
                                ...ratingConfig,
                                min: ratingConfig?.min || 1,
                                max: ratingConfig?.max || 10,
                                labels: {
                                  min: e.target.value,
                                  max: ratingConfig?.labels?.max || 'Extremely',
                                },
                              } as RatingConfig,
                            })
                          }
                          placeholder="Not at all"
                          className="w-full px-2 py-1 border rounded"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Max Label</label>
                        <input
                          type="text"
                          value={ratingConfig?.labels?.max || 'Extremely'}
                          onChange={(e) =>
                            updateQuestion(index, {
                              config: {
                                ...ratingConfig,
                                min: ratingConfig?.min || 1,
                                max: ratingConfig?.max || 10,
                                labels: {
                                  min: ratingConfig?.labels?.min || 'Not at all',
                                  max: e.target.value,
                                },
                              } as RatingConfig,
                            })
                          }
                          placeholder="Extremely"
                          className="w-full px-2 py-1 border rounded"
                        />
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Info for text */}
              {question.type === 'text' && (
                <p className="text-xs text-gray-500">Text area with 0-512 character limit</p>
              )}
            </div>

            <button
              onClick={() => deleteQuestion(index)}
              className="text-red-600 hover:text-red-800 mt-2"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}

      {/* Add Question Buttons */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Button onClick={addRatingQuestion} variant="outline" className="flex-1 flex items-center justify-center gap-2">
          <Plus className="w-4 h-4" />
          Add Rating
        </Button>
        <Button onClick={addMultiSelectQuestion} variant="outline" className="flex-1 flex items-center justify-center gap-2">
          <Plus className="w-4 h-4" />
          Add Multi-Select
        </Button>
        <Button onClick={addTextQuestion} variant="outline" className="flex-1 flex items-center justify-center gap-2">
          <Plus className="w-4 h-4" />
          Add Text
        </Button>
      </div>
    </div>
  );
};
